import type { ActionButton } from '@app/src/components/ActionButtons'
import type { TabsProps } from '@app/src/components/Tabs'
import { useRiskSettings } from '@app/src/context/RiskSettingsContext'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import { useSustainabilityTopics } from '@app/src/hooks/useSustainabilityTopics'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import type { ConfigurationsParams } from '@app/src/pages/Configurations/ConfigurationsScene'
import { insertIf } from '@app/src/utils/helpersTs'
import { Edit as EditIcon, WidgetsOutlined } from '@mui/icons-material'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from '@mui/material'
import type { Location } from 'history'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useParams, useRouteMatch } from 'react-router-dom'
import RiskIndicesCards from './Cards/RiskIndicesCards'
import { partitionRiskSettings } from './Cards/riskIndexCards'
import RiskScreeningInfoDialog from './RiskScreeningInfoDialog'
import StandaloneRiskIndices from './StandaloneRiskIndices'
import { useRiskSettingsEditor } from './useRiskSettingsEditor'

enum InherentRiskTab {
  Default = 'default',
  Standalone = 'standalone',
}

const InherentRiskTabs: React.FC = () => {
  const { formatMessage } = useIntl()
  const { path } = useRouteMatch()
  const { configurationsPage, configurationsSubPage } = useParams<ConfigurationsParams>()
  const { riskSettings, isLoadingRiskSettings, isErrorRiskSettings } = useRiskSettings()
  const { topics, isLoading: isLoadingTopics, isError: isErrorTopics } = useSustainabilityTopics()
  const [open, openDialog, closeDialog] = useDialogState()

  // Switching between the two tabs is a history push, which the unsaved-changes guard would
  // otherwise treat as leaving the page.
  const riskScreeningPath = generatePath(path, { configurationsPage })
  const shouldBlockLocation = (nextLocation: Location) => !nextLocation.pathname.startsWith(riskScreeningPath)

  const editor = useRiskSettingsEditor(riskSettings, { shouldBlockLocation })

  const { topicCards, standaloneSettings } = useMemo(
    () => partitionRiskSettings(editor.effectiveSettings, topics),
    [editor.effectiveSettings, topics],
  )

  const isStandaloneTab = configurationsSubPage === InherentRiskTab.Standalone
  const isLoading = isLoadingRiskSettings || isLoadingTopics
  // Without the topics we can't tell a topic-linked index from a standalone one, so both tabs
  // would render a confident but wrong split.
  const isError = isErrorRiskSettings || isErrorTopics

  const actionTabs: TabsProps['tabs'] = [
    {
      type: InherentRiskTab.Default,
      url: InherentRiskTab.Default,
      label: formatMessage({ id: 'general.default' }),
      skipQueryParams: true,
    },
    {
      type: InherentRiskTab.Standalone,
      url: InherentRiskTab.Standalone,
      label: formatMessage({ id: 'riskScreeningConfig.tabs.standalone' }),
      skipQueryParams: true,
    },
  ]

  return (
    <>
      <ConfigurationCollection
        title={formatMessage({ id: 'navbar.riskScreening' })}
        actionTabs={actionTabs}
        actionButtons={[
          {
            label: formatMessage({ id: 'topicalRisk.methodology.title' }),
            variant: 'text',
            onClick: openDialog,
            startIcon: <WidgetsOutlined />,
          },
          ...insertIf<ActionButton>(editor.isEditMode, {
            label: formatMessage({ id: 'general.cancel' }),
            variant: 'outlined',
            onClick: editor.cancelEdit,
          }),
          {
            label: editor.isEditMode
              ? editor.hasChanges
                ? formatMessage({ id: 'general.saveChanges' })
                : formatMessage({ id: 'general.save' })
              : formatMessage({ id: 'general.edit' }),
            variant: 'contained',
            startIcon: editor.isEditMode ? undefined : <EditIcon />,
            onClick: editor.isEditMode ? () => editor.save() : editor.startEdit,
            disabled: (editor.isEditMode && !editor.hasChanges) || editor.isSaving,
          },
        ]}
        enablePadding={{ top: false, right: true }}
      >
        <Typography color="text.secondary" pt={2} pb={2}>
          {formatMessage({
            id: isStandaloneTab
              ? 'riskScreeningConfig.tabs.standaloneDescription'
              : 'riskScreeningConfig.tabs.defaultDescription',
          })}
        </Typography>

        {isStandaloneTab ? (
          <StandaloneRiskIndices
            settings={standaloneSettings}
            isEditMode={editor.isEditMode}
            isLoading={isLoading}
            isError={isError}
            onToggleChange={editor.toggleIndex}
            modifiedRiskStates={editor.modifiedRiskStates}
          />
        ) : (
          <RiskIndicesCards
            cards={topicCards}
            isEditMode={editor.isEditMode}
            isLoading={isLoading}
            isError={isError}
            onToggleIndex={editor.toggleIndex}
            onHowThisWorks={openDialog}
          />
        )}
      </ConfigurationCollection>

      <RiskScreeningInfoDialog open={open} closeDialog={closeDialog} />

      <Dialog open={editor.isBlocked} onClose={editor.stayOnPage}>
        <DialogTitle>{formatMessage({ id: 'general.youHaveUnsavedChanges' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{formatMessage({ id: 'general.unsavedChanges' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={editor.discardAndLeave} variant="text">
            {formatMessage({ id: 'resourceCollections.update.discardChanges' })}
          </Button>
          <Button onClick={() => editor.save(true)} variant="outlined">
            {formatMessage({ id: 'general.saveAndContinue' })}
          </Button>
          <Button onClick={editor.stayOnPage} variant="contained" autoFocus>
            {formatMessage({ id: 'general.stayOnPage' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default InherentRiskTabs
